import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, output } from '@angular/core';

export type NoticeTone = 'info' | 'success' | 'warning' | 'error';
export type NoticeRole = 'alert' | 'status' | 'none';

/**
 * A message block: an icon, the projected text and an optional
 * `.notice-actions` row under it. Errors are announced assertively, other
 * tones politely; `role` overrides that. A dismissible notice keeps its close
 * button slot, so the text never reflows when it appears.
 */
@Component({
  selector: 'app-notice, app-auth-notice',
  templateUrl: './notice.component.html',
  styleUrl: './notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.role]': "resolvedRole() === 'none' ? null : resolvedRole()",
    '[class.notice--info]': "tone() === 'info'",
    '[class.notice--success]': "tone() === 'success'",
    '[class.notice--warning]': "tone() === 'warning'",
    '[class.notice--error]': "tone() === 'error'",
    '[class.notice--dismissible]': 'dismissible()',
  },
})
export class NoticeComponent {
  readonly tone = input<NoticeTone>('info');
  readonly role = input<NoticeRole | null>(null);
  readonly dismissible = input(false, { transform: booleanAttribute });
  /** Accessible name of the dismiss button. */
  readonly dismissLabel = input('Dismiss');

  readonly dismiss = output<void>();

  readonly resolvedRole = computed<NoticeRole>(() => this.role() ?? (this.tone() === 'error' ? 'alert' : 'status'));
}
